import React from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { Input, Select, Box, Grid, GridItem, Button, useToast, Text, Checkbox } from '@chakra-ui/react';
import * as Yup from 'yup';
import DatePicker from 'components/datePicker/DatePicker';
import xhr from 'xhr';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import SharedClassPicker from './SharedClassPicker';

export default function ClassRecurrent(props) {
  const { semester, setReload, title, user, setIsOpen } = props;
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = React.useState(false);
  const [dateFilter, setDateFilter] = React.useState(null);
  const [repeatUntilDate, setRepeatUntilDate] = React.useState(null);
  const toast = useToast();

  // Clase compartida con otros programas (grados o carreras técnicas).
  const canShare = !user?.school && ['grade', 'technical_career'].includes(searchParams.get('type'));
  const [sharedEnabled, setSharedEnabled] = React.useState(false);
  const [otherSubjects, setOtherSubjects] = React.useState([]);
  const [otherClassGroups, setOtherClassGroups] = React.useState([]);
  const [sharedValid, setSharedValid] = React.useState(true);

  const handleDateChange = (date) => {
    setDateFilter(date);
  };

  const handleRepeatUntilChange = (date) => {
    setRepeatUntilDate(date);
  };

  const BasicInfoSchemaCreate = Yup.object().shape({
    title: Yup.string().required('Requerido'),
    hour: Yup.string().required('Requerido'),
    minutes: Yup.string().required('Requerido'),
    iframe_code: Yup.string().required('Requerido'),
    type: Yup.string().required('Requerido'),
    repeat_day: Yup.string().required('Requerido'),
  });

  function horas() {
    let diasV = [];
    for (let step = 5; diasV.length < 17; step++) {
      if (step <= 9) {
        diasV.push({
          value: `0${step}`,
          label: step,
        });
      } else {
        diasV.push({
          value: `${step}`,
          label: step,
        });
      }
    }

    return diasV;
  }

  const daysOfWeek = [
    { value: '1', label: 'Lunes' },
    { value: '2', label: 'Martes' },
    { value: '3', label: 'Miércoles' },
    { value: '4', label: 'Jueves' },
    { value: '5', label: 'Viernes' },
    { value: '6', label: 'Sábado' },
    { value: '0', label: 'Domingo' },
  ];

  const buildStartDate = (baseDate, hourValue, minuteValue) => {
    let formatDate = dayjs(baseDate).format('YYYY-MM-DD');

    function addHours() {
      let hour = Number(hourValue) + 5;
      switch (hour) {
        case 25:
          hour = '01';
          break;
        case 26:
          hour = '02';
          break;
        case 27:
          hour = '03';
          break;
        default:
          hour = String(hour).padStart(2, '0');
          break;
      }
      return hour;
    }

    let tiempo = `${addHours()}:${minuteValue}`;

    if (tiempo === '24:30') {
      tiempo = '00:30';
      formatDate = dayjs(baseDate).add(1, 'day').format('YYYY-MM-DD');
    } else if (Number(hourValue) >= 20) {
      formatDate = dayjs(baseDate).add(1, 'day').format('YYYY-MM-DD');
    }

    return `${formatDate}T${tiempo}:00.000Z`;
  };

  const generateRecurringDates = (startDate, endDate, repeatDay) => {
    const dates = [];
    let current = dayjs(startDate).startOf('day');
    const limit = dayjs(endDate).endOf('day');

    while (current.isBefore(limit) || current.isSame(limit, 'day')) {
      if (current.day() === Number(repeatDay)) {
        dates.push(current.toDate());
      }
      current = current.add(1, 'day');
    }

    return dates;
  };

  const handleInsertRecurringClasses = async (values, action) => {
    try {
      setLoading(true);

      if (!dateFilter) {
        window.alert('Debes seleccionar la fecha inicial');
        return;
      }

      if (!repeatUntilDate) {
        window.alert('Debes seleccionar la fecha final');
        return;
      }

      if (dayjs(repeatUntilDate).isBefore(dayjs(dateFilter), 'day')) {
        window.alert('La fecha final no puede ser menor a la fecha inicial');
        return;
      }

      // Sin materia adicional la grabación no le aparece a nadie de ese programa.
      if (sharedEnabled && (!otherSubjects.length || !sharedValid)) {
        toast({
          title: 'Cada programa agregado necesita al menos una materia, o desmarca la clase compartida.',
          status: 'warning',
          duration: 4000,
          isClosable: true,
        });
        return;
      }

      const recurringDates = generateRecurringDates(dateFilter, repeatUntilDate, values.repeat_day);

      if (!recurringDates.length) {
        window.alert('No se encontraron fechas para la recurrencia seleccionada');
        return;
      }

      const { period, semester, repeat_day, ...rest } = values;

      const baseData = {
        ...rest,
        subject: searchParams.get('subject'),
        class_group: searchParams.get('group'),
        [searchParams.get('type')]: searchParams.get('program'),
        school: user?.school?.id || null,
      };

      if (period) baseData.period = period;
      if (semester) baseData.semester = semester;

      // Clase compartida: materias y grupos de otros programas.
      if (sharedEnabled && otherSubjects.length) baseData.other_subjects = otherSubjects;
      if (sharedEnabled && otherClassGroups.length) baseData.other_class_groups = otherClassGroups;

      for (const classDate of recurringDates) {
        const start_date = buildStartDate(classDate, values.hour, values.minutes);

        await xhr.post('/live-classes/', {
          data: {
            ...baseData,
            start_date,
          },
        });
      }

      setReload(true);

      toast({
        title: `Se crearon ${recurringDates.length} clases correctamente`,
        status: 'success',
        duration: 2500,
        isClosable: true,
      });

      action.resetForm();
      setDateFilter(null);
      setRepeatUntilDate(null);

      if (setIsOpen) setIsOpen(false);
    } catch (error) {
      console.error(error);
      toast({
        title: 'Hubo un error al crear las clases recurrentes',
        status: 'error',
        duration: 2500,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box maxHeight="650px" minH="650px" overflowY="auto">
      <Box display="flex" justifyContent="space-between" borderBottom="1px solid gray" marginBottom="20px">
        <Box>
          <Text fontWeight="bold" fontSize="20px">
            Crear reunión recurrente
          </Text>
        </Box>
      </Box>

      <Formik
        initialValues={{
          title: title || '',
          hour: '',
          minutes: '',
          semester: searchParams.get('semester') || '',
          iframe_code: '',
          type: 'zoom',
          period: searchParams.get('period') || '',
          repeat_day: '',
        }}
        validationSchema={BasicInfoSchemaCreate}
        onSubmit={handleInsertRecurringClasses}
        enableReinitialize
      >
        {(data) => {
          return (
            <Form>
              <Grid gap={5}>
                <GridItem colSpan={6}>
                  <Box mb="5px">Fecha inicial</Box>
                  <DatePicker date={dateFilter} placeholder="Fecha inicial" onChange={handleDateChange} />
                </GridItem>

                <GridItem colSpan={6}>
                  <Box mb="5px">Repetir hasta</Box>
                  <DatePicker
                    date={repeatUntilDate}
                    placeholder="Fecha final"
                    onChange={handleRepeatUntilChange}
                  />
                </GridItem>

                <GridItem colSpan={4}>
                  <Box mb="5px">Hora</Box>
                  <Field name="hour" as={Select} placeholder="Hora">
                    {horas().map((el) => (
                      <option value={el.value} key={el.value}>
                        {el.label}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage name="hour">
                    {(msg) => <div style={{ color: 'red', fontSize: 13 }}>{msg}</div>}
                  </ErrorMessage>
                </GridItem>

                <GridItem colSpan={4}>
                  <Box mb="5px">Minutos</Box>
                  <Field name="minutes" as={Select} placeholder="Minuto">
                    <option value="00">00</option>
                    <option value="30">30</option>
                  </Field>
                  <ErrorMessage name="minutes">
                    {(msg) => <div style={{ color: 'red', fontSize: 13 }}>{msg}</div>}
                  </ErrorMessage>
                </GridItem>

                <GridItem colSpan={4}>
                  <Box mb="5px">Día de repetición</Box>
                  <Field name="repeat_day" as={Select} placeholder="Seleccionar día">
                    {daysOfWeek.map((el) => (
                      <option value={el.value} key={el.value}>
                        {el.label}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage name="repeat_day">
                    {(msg) => <div style={{ color: 'red', fontSize: 13 }}>{msg}</div>}
                  </ErrorMessage>
                </GridItem>

                <GridItem colSpan={12}>
                  <Field name="title" label="Titulo" color="black" placeholder="Titulo" as={Input} />
                  <ErrorMessage name="title">
                    {(msg) => <div style={{ color: 'red', fontSize: 13 }}>{msg}</div>}
                  </ErrorMessage>
                </GridItem>

                <GridItem colSpan={12}>
                  <Field name="iframe_code" label="Iframe" color="black" placeholder="Iframe" as={Input} />
                  <ErrorMessage name="iframe_code">
                    {(msg) => <div style={{ color: 'red', fontSize: 13 }}>{msg}</div>}
                  </ErrorMessage>
                </GridItem>

                <GridItem colSpan={4}>
                  <Field name="type" as={Select} placeholder="Tipo" label="Tipo">
                    <option value="zoom">Zoom</option>
                    <option value="googlemeet">Google Meet</option>
                    <option value="clickmeeting">Click Meeting</option>
                  </Field>
                  <ErrorMessage name="type">
                    {(msg) => <div style={{ color: 'red', fontSize: 13 }}>{msg}</div>}
                  </ErrorMessage>
                </GridItem>

                <GridItem colSpan={4}>
                  <Field name="period" placeholder="Periodo" as={Select}>
                    <option value="">Seleccionar</option>
                    <option value="P1">Periodo 1</option>
                    <option value="P2">Periodo 2</option>
                    <option value="P3">Periodo 3</option>
                    <option value="P4">Periodo 4</option>
                  </Field>
                </GridItem>

                <GridItem colSpan={4}>
                  <Field name="semester" placeholder="Semestre" as={Select}>
                    <option value="">Seleccionar</option>
                    {semester?.map((el) => (
                      <option key={el.value} value={el.value}>
                        {el.label}
                      </option>
                    ))}
                  </Field>
                </GridItem>
              </Grid>

              {canShare && (
                <Box mt="16px">
                  <Checkbox
                    isChecked={sharedEnabled}
                    onChange={(e) => setSharedEnabled(e.target.checked)}
                    colorScheme="purple"
                  >
                    Esta clase es compartida con otros programas
                  </Checkbox>
                  {sharedEnabled && (
                    <Box mt="10px">
                      <SharedClassPicker
                        currentProgramType={searchParams.get('type')}
                        currentProgramId={searchParams.get('program')}
                        onChange={({ other_subjects, other_class_groups, valid }) => {
                          setOtherSubjects(other_subjects);
                          setOtherClassGroups(other_class_groups);
                          setSharedValid(valid);
                        }}
                      />
                    </Box>
                  )}
                </Box>
              )}

              <br />

              <Box display="flex" justifyContent="space-between">
                <Button type="submit" colorScheme="#574feb" bg="#574feb" width="180px" isLoading={loading}>
                  Crear recurrentes
                </Button>

                {setIsOpen ? (
                  <Button
                    onClick={() => {
                      setIsOpen(false);
                    }}
                    colorScheme="red"
                    variant="outline"
                    width="140px"
                  >
                    Cancelar
                  </Button>
                ) : null}
              </Box>
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
}